import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, File } from 'phosphor-react';
import { cn } from '../utils/cn';

const cssId = 'dropzone-sparkle-styles-v4';

const injectStyles = () => {
  if (document.getElementById(cssId)) return;
  const style = document.createElement('style');
  style.id = cssId;
  style.textContent = `
    @keyframes dzRotateBorder {
      to { transform: translate(-50%,-50%) rotate(1turn); }
    }
    @keyframes dzShineSweep {
      0%                { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
      20%               { opacity: .9; }
      45%, 100%         { transform: translateX(120%) skewX(-18deg); opacity: 0; }
    }
    @keyframes dzSparkleIdle {
      0%, 85%  { opacity:0; transform:scale(.2) rotate(0deg);   filter:blur(1px); }
      88%      { opacity:1; transform:scale(1.35) rotate(35deg); filter:blur(0); }
      91%      { opacity:.35; transform:scale(.75) rotate(75deg); }
      94%      { opacity:1; transform:scale(1.8) rotate(120deg); }
      97%, 100%{ opacity:0; transform:scale(.25) rotate(180deg); filter:blur(1.5px); }
    }
    @keyframes dzSparkleActive {
      0%,100% { opacity:0; transform:scale(.2) rotate(0deg);   filter:blur(1px); }
      18%     { opacity:1; transform:scale(1.35) rotate(35deg); filter:blur(0); }
      36%     { opacity:.35; transform:scale(.75) rotate(75deg); }
      54%     { opacity:1; transform:scale(1.8) rotate(120deg); }
      72%     { opacity:0; transform:scale(.25) rotate(180deg); filter:blur(1.5px); }
    }
    .dz-rotate      { animation: dzRotateBorder 4s linear infinite; }
    .dz-shine-idle  { animation: dzShineSweep 10s ease-in-out infinite; }
    .dz-shine-hover { animation: dzShineSweep 3.8s ease-in-out infinite; }

    .dz-sp-idle-1  { animation: dzSparkleIdle 10s 0s    infinite ease-in-out; }
    .dz-sp-idle-2  { animation: dzSparkleIdle 10s 0.1s  infinite ease-in-out; }
    .dz-sp-idle-3  { animation: dzSparkleIdle 10s 0.2s  infinite ease-in-out; }
    .dz-sp-idle-4  { animation: dzSparkleIdle 10s 0.3s  infinite ease-in-out; }
    .dz-sp-idle-5  { animation: dzSparkleIdle 10s 0.4s  infinite ease-in-out; }
    .dz-sp-idle-6  { animation: dzSparkleIdle 10s 0.5s  infinite ease-in-out; }
    .dz-sp-idle-7  { animation: dzSparkleIdle 10s 0.6s  infinite ease-in-out; }
    .dz-sp-idle-8  { animation: dzSparkleIdle 10s 0.7s  infinite ease-in-out; }

    .dz-sp-1  { animation: dzSparkleActive 4s .1s  infinite ease-in-out; }
    .dz-sp-2  { animation: dzSparkleActive 4s .8s  infinite ease-in-out; }
    .dz-sp-3  { animation: dzSparkleActive 4s 1.6s infinite ease-in-out; }
    .dz-sp-4  { animation: dzSparkleActive 4s .4s  infinite ease-in-out; }
    .dz-sp-5  { animation: dzSparkleActive 4s 2.1s infinite ease-in-out; }
    .dz-sp-6  { animation: dzSparkleActive 4s 1.1s infinite ease-in-out; }
    .dz-sp-7  { animation: dzSparkleActive 4s 2.8s infinite ease-in-out; }
    .dz-sp-8  { animation: dzSparkleActive 4s .6s  infinite ease-in-out; }
  `;
  document.head.appendChild(style);
};

const SPARKLES = [
  { left: '8%',  top: '18%', idleCls: 'dz-sp-idle-1', activeCls: 'dz-sp-1' },
  { left: '22%', top: '72%', idleCls: 'dz-sp-idle-2', activeCls: 'dz-sp-2' },
  { left: '38%', top: '30%', idleCls: 'dz-sp-idle-3', activeCls: 'dz-sp-3' },
  { left: '55%', top: '78%', idleCls: 'dz-sp-idle-4', activeCls: 'dz-sp-4' },
  { left: '68%', top: '15%', idleCls: 'dz-sp-idle-5', activeCls: 'dz-sp-5' },
  { left: '80%', top: '58%', idleCls: 'dz-sp-idle-6', activeCls: 'dz-sp-6' },
  { left: '91%', top: '34%', idleCls: 'dz-sp-idle-7', activeCls: 'dz-sp-7' },
  { left: '46%', top: '50%', idleCls: 'dz-sp-idle-8', activeCls: 'dz-sp-8' },
];

const BANNER_BG = 'radial-gradient(circle at 20% 20%, rgba(255,255,255,.18), transparent 24%), radial-gradient(circle at 80% 30%, rgba(25,118,237,.22), transparent 28%), radial-gradient(circle at 50% 90%, rgba(89,204,224,.18), transparent 32%), linear-gradient(135deg, #080812, #111122 45%, #07070d)';

interface FileDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  acceptedFileTypes?: Record<string, string[]>;
  maxFiles?: number;
  maxSize?: number;
  className?: string;
  label?: string;
  labelActive?: string;
  showIcon?: boolean;
  showSelectedFiles?: boolean;
}

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFilesSelected,
  acceptedFileTypes = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    'text/csv': ['.csv'],
  },
  maxFiles,
  maxSize = 10 * 1024 * 1024,
  className = '',
  label,
  labelActive = 'Suelta aquí',
  showIcon = true,
  showSelectedFiles = true,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isHovered, setIsHovered] = useState(false);

  React.useEffect(() => { injectStyles(); }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      if (rejectedFiles.length > 0) {
        const newErrors = rejectedFiles.map(({ file, errors }) => {
          if (errors.some((e: any) => e.code === 'file-too-large'))
            return `${file.name}: Archivo muy grande (máx. ${(maxSize / 1024 / 1024).toFixed(0)}MB)`;
          if (errors.some((e: any) => e.code === 'file-invalid-type'))
            return `${file.name}: Tipo de archivo no permitido`;
          return `${file.name}: Error al cargar`;
        });
        setErrors(newErrors);
      } else {
        setErrors([]);
      }
      const newFiles = maxFiles ? acceptedFiles.slice(0, maxFiles) : acceptedFiles;
      setFiles(newFiles);
      onFilesSelected(newFiles);
    },
    [maxFiles, maxSize, onFilesSelected]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxSize,
    multiple: true,
  });

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    onFilesSelected(newFiles);
    setErrors([]);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const active = isHovered || isDragActive;

  return (
    <div className={cn('flex flex-col gap-4 h-full', className)}>
      <div
        {...getRootProps()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          'border-2 border-dashed rounded-lg text-center cursor-pointer flex-1 flex flex-col items-center justify-center',
          showIcon ? 'px-8 pt-10 pb-12' : 'px-4 pt-6 pb-8',
          'border-border',
          className
        )}
        style={{
          position: 'relative', overflow: 'hidden',
          minHeight: 80,
          borderColor: 'rgba(0,0,0,0.12)',
        }}
      >
        <input {...getInputProps()} />

        {/* Dark background overlay — fades in/out on hover */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
          background: BANNER_BG,
          opacity: active ? 1 : 0,
          transition: 'opacity 500ms ease',
        }} />

        {/* Shine sweep */}
        <div
          className={active ? 'dz-shine-hover' : 'dz-shine-idle'}
          style={{
            position: 'absolute', inset: 0, zIndex: 4,
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 25%, rgba(255,255,255,.55) 50%, rgba(255,255,255,0) 75%, transparent 100%)',
            pointerEvents: 'none', mixBlendMode: 'screen',
            opacity: active ? 1 : 0, transition: 'opacity 400ms ease',
          }}
        />

        {/* Idle sparkles — only mounted when not active */}
        {!active && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
            {SPARKLES.map((s, i) => (
              <span key={i} className={s.idleCls} style={{ position: 'absolute', left: s.left, top: s.top, width: 5, height: 5, borderRadius: 999, background: 'white', opacity: 0, boxShadow: '0 0 8px white, 0 0 18px rgba(255,255,255,.9), 0 0 34px rgba(89,204,224,.75)' }} />
            ))}
          </div>
        )}

        {/* Active sparkles — only mounted when hovering */}
        {active && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
            {SPARKLES.map((s, i) => (
              <span key={i} className={s.activeCls} style={{ position: 'absolute', left: s.left, top: s.top, width: 5, height: 5, borderRadius: 999, background: 'white', opacity: 0, boxShadow: '0 0 8px white, 0 0 18px rgba(255,255,255,.9), 0 0 34px rgba(89,204,224,.75)' }} />
            ))}
          </div>
        )}

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 5 }} className={cn('flex flex-col items-center w-full', showIcon ? 'gap-3' : 'gap-0')}>
          {showIcon && (
            <Upload
              size={32}
              weight="bold"
              style={{ transition: 'color 500ms ease' }}
              className={cn('flex-shrink-0', active ? 'text-white' : 'text-text-subtle')}
            />
          )}
          <div style={{ perspective: '600px' }} className="w-full px-4">
            <div className="relative min-h-[2.5rem] flex items-center justify-center" style={{ maxWidth: '100%' }}>
              <p
                className="font-sans font-medium text-sm origin-bottom text-center break-words w-full"
                style={{
                  color: active ? 'rgba(255,255,255,.9)' : undefined,
                  transform: active ? 'translateY(50%) rotateX(90deg)' : 'translateY(0) rotateX(0deg)',
                  opacity: active ? 0 : 1,
                  transition: 'transform 500ms ease, opacity 250ms ease, color 500ms ease',
                  visibility: active ? 'hidden' : 'visible',
                }}
              >
                {label ?? 'Arrastra archivos aquí o haz clic para seleccionar'}
              </p>
              <p
                className="font-sans font-semibold text-sm absolute inset-0 flex items-center justify-center origin-top text-center break-words"
                style={{
                  color: 'rgba(255,255,255,.9)',
                  transform: active ? 'translateY(0) rotateX(0deg)' : 'translateY(-50%) rotateX(90deg)',
                  opacity: active ? 1 : 0,
                  transition: 'transform 500ms ease, opacity 40ms ease',
                }}
              >
                Vamos
              </p>
            </div>
            {maxSize && (
              <p
                className="font-sans font-normal text-xs -mt-3 text-center"
                style={{ color: active ? 'rgba(255,255,255,.6)' : undefined, transition: 'color 500ms ease' }}
              >
                Máximo {(maxSize / 1024 / 1024).toFixed(0)}MB
              </p>
            )}
          </div>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="font-sans font-semibold text-sm text-red-600 mb-1">Errores:</p>
          <ul className="list-disc list-inside space-y-1">
            {errors.map((error, index) => (
              <li key={index} className="font-sans font-normal text-xs text-red-600">{error}</li>
            ))}
          </ul>
        </div>
      )}

      {showSelectedFiles && files.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="font-sans font-semibold text-sm text-text">
            Archivos seleccionados ({files.length}{maxFiles && ` / ${maxFiles}`})
          </p>
          <div className="flex flex-col gap-2">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between p-3 bg-background border border-border rounded-lg"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <File size={20} weight="bold" className="text-text-subtle flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-sans font-medium text-sm text-text truncate">{file.name}</p>
                    <p className="font-sans font-normal text-xs text-text-subtle">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                  className="ml-2 p-1.5 hover:bg-muted rounded transition-colors flex-shrink-0"
                  aria-label={`Eliminar ${file.name}`}
                >
                  <X size={16} weight="bold" className="text-text-subtle" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileDropzone;
